/**
 * SOUND-777 — transaction-type percentages are bounded 0–100.
 *
 * The registration form had no numeric bounds at all, so a merchant could key
 * "-20" for swiped and "120" for e-commerce. Kurv's Stratus validation rejects
 * those, and ~20% of prod registrations would fail it on this alone (the plan,
 * §3.2). The rule is enforced in two places that must agree — the merchant
 * wizard (StepRenderer / useRegistration / ApplicationEditor) and the
 * Registration tab's inline editor (RecordFieldRow) — so it lives here, once,
 * with tests.
 *
 * WHICH FIELDS
 * ------------
 * Identified from the SCHEMA the form reads, never from a hand list of names:
 * every field in the `transactionTypes` group (that group IS "Transaction Types
 * (Percentages)"), plus any field whose schema declares `unit: "percent"`. If
 * sound-api starts declaring `min`/`max` on those fields, they are honoured
 * without a client change.
 *
 * BLANK IS NOT AN ERROR. These fields are optional; an unanswered percentage is
 * a completeness question, not a bounds one.
 */

export const PERCENT_GROUP = "transactionTypes";
export const PERCENT_MIN = 0;
export const PERCENT_MAX = 100;

/** Does this schema field (or record-view descriptor) hold a percentage? */
export function isPercentField(field) {
  if (!field) return false;
  if (field.unit === "percent") return true;
  if (field.group === PERCENT_GROUP) return true;
  return false;
}

/** The bounds for a field: the schema's own if it declares them, else 0–100. */
export function percentBoundsFor(field) {
  return {
    min: declaredNumber(field?.min, PERCENT_MIN),
    max: declaredNumber(field?.max, PERCENT_MAX),
  };
}

function declaredNumber(v, fallback) {
  if (isBlank(v)) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

/**
 * Check one value. Codes mirror sound-api's validator: `out_of_range` is the
 * ONLY code relaxed for unchanged values (enforceRangeOnChangedValues);
 * `not_a_number` keeps blocking, as the API's "must be a number" does.
 *
 * @returns {null | { code: "out_of_range" | "not_a_number", message: string }}
 */
export function percentCheck(value, field) {
  if (isBlank(value)) return null;
  const { min, max } = percentBoundsFor(field);
  const n = Number(String(value).trim());
  if (!Number.isFinite(n)) return { code: "not_a_number", message: "Enter a number" };
  if (n < min) return { code: "out_of_range", message: `Must be ${min}% or more` };
  if (n > max) return { code: "out_of_range", message: `Must be ${max}% or less` };
  return null;
}

/**
 * The inline error for one value, or null when it is acceptable. Ignores the
 * stored value — use this where a single field is being edited on purpose (the
 * Registration tab's inline editor), so any out-of-range value it would send is
 * a change and must block.
 */
export function percentError(value, field) {
  return percentCheck(value, field)?.message || null;
}

/**
 * Mirrors `sameValue` in sound-api utilities/applicationValidator.mjs: blanks
 * are equal to each other, and "-9" equals -9.
 */
export function sameValue(a, b) {
  if (a === b) return true;
  if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b);
  const na = Number(a);
  const nb = Number(b);
  return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
}

/**
 * SOUND-777 — mirrors sound-api `enforceRangeOnChangedValues`.
 *
 * The wizard and ApplicationEditor PUT the WHOLE record, and 28 registrations
 * already store values like -9. A whole-record check would make those records
 * unsaveable for an unrelated edit. So an out_of_range value that EQUALS what
 * was loaded is a soft WARNING (shown, never blocking); a changed value —
 * including a new value that is still out of range — is a blocking ERROR.
 *
 * `hasStored` false (nothing loaded yet / a create) means every problem blocks,
 * exactly as the API does with no stored document.
 *
 * @returns {{ error: string|null, warning: string|null, code: string|null }}
 */
export function percentStatus(value, field, { storedValue, hasStored = false } = {}) {
  const check = percentCheck(value, field);
  if (!check) return { error: null, warning: null, code: null };
  if (check.code === "out_of_range" && hasStored && sameValue(value, storedValue)) {
    return {
      error: null,
      warning: `${check.message} — this is the value already on record. Please correct it.`,
      code: check.code,
    };
  }
  return { error: check.message, warning: null, code: check.code };
}

/** percentStatus for any field; both null for a non-percentage field. */
export function fieldPercentStatus(field, value, opts) {
  return isPercentField(field) ? percentStatus(value, field, opts) : { error: null, warning: null, code: null };
}

/**
 * The error for a field only if it is a percentage field; null otherwise.
 * Stored-value-blind — see percentError.
 */
export function fieldPercentError(field, value) {
  return isPercentField(field) ? percentError(value, field) : null;
}

/**
 * BLOCKING errors for one flat section: `{ fieldName: message }`. With a
 * stored section, unchanged out-of-range values are left out (they warn, they
 * do not block). Omit `storedSection` to check everything.
 */
export function sectionPercentErrors(fields, sectionData, storedSection) {
  const hasStored = storedSection !== undefined && storedSection !== null;
  const out = {};
  for (const f of fields || []) {
    if (!isPercentField(f)) continue;
    const { error } = percentStatus(sectionData?.[f.name], f, {
      storedValue: hasStored ? storedSection[f.name] : undefined,
      hasStored,
    });
    if (error) out[f.name] = error;
  }
  return out;
}

/**
 * Every BLOCKING percentage problem across a whole application, for the save
 * guards. `stored` is the document as loaded from the server; when given,
 * unchanged out-of-range values do not block (sound-api drops them too).
 * Repeatable steps are skipped — none carry a transaction-type percentage.
 *
 * @returns {Array<{ stepKey, field, label, message, code }>}
 */
export function applicationPercentErrors(schema, data, stored = null) {
  const out = [];
  if (!schema || !Array.isArray(schema.steps) || !data) return out;
  for (const step of schema.steps) {
    if (step.sectionType === "repeatable") continue;
    const section = data[step.key] || {};
    const storedSection = stored ? stored[step.key] || {} : undefined;
    for (const f of step.fields || []) {
      if (!isPercentField(f)) continue;
      const { error, code } = percentStatus(section[f.name], f, {
        storedValue: storedSection ? storedSection[f.name] : undefined,
        hasStored: Boolean(stored),
      });
      if (error) {
        out.push({ stepKey: step.key, field: f.name, label: f.label || f.name, message: error, code });
      }
    }
  }
  return out;
}

/** One-line summary for a toast: "E-Commerce/Online (%): Must be 100% or less". */
export function describePercentErrors(errors, limit = 3) {
  if (!errors?.length) return "";
  const shown = errors.slice(0, limit).map((e) => `${e.label}: ${e.message}`);
  const rest = errors.length - shown.length;
  return shown.join("; ") + (rest > 0 ? ` (+${rest} more)` : "");
}
